import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { TabProvider } from "@/contexts/TabContext";
import { TabBar } from "@/components/tabs/TabBar";
import { TabRenderer } from "@/components/tabs/TabRenderer";
import { TabNavLink } from "@/components/tabs/TabNavLink";
import { controllerTabRegistry } from "@/tabs/controllerTabRegistry";
import { useAuth } from "@/contexts/AuthContext";
import { useBillingCycleNotifications } from "@/hooks/useBillingCycleNotifications";
import { useCalendarRealtime } from "@/hooks/useCalendarRealtime";
import { usePermissions } from "@/hooks/usePermissions";
import { useApprovalPendingCount } from "@/hooks/useApprovalSystem";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { CompanySelectorModal } from "@/components/CompanySelectorModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NotificationBell } from "@/components/controller/NotificationBell";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, FolderOpen, ClipboardCheck, Receipt,
  CalendarClock, CalendarDays, Settings, LogOut, Users, Truck, FileText, CreditCard,
  Bell, Briefcase, ShieldCheck, ScrollText, FileSpreadsheet, Menu, Building2, MessageSquare,
  UsersRound,
  TicketCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCommandXLogo } from "@/hooks/useCommandXLogo";
import { ReportIssueDialog } from "@/components/controller/ReportIssueDialog";
import { HelpCircle } from "lucide-react";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  anyPermission?: string[];
}

const unifiedNav: { label: string; items: NavItem[] }[] = [
  {
    label: "Home",
    items: [
      { to: "/controller", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/controller/calendar", label: "Calendar", icon: CalendarDays },
      { to: "/controller/comms", label: "Comms", icon: MessageSquare },
      { to: "/controller/notifications", label: "Notifications", icon: Bell },
    ],
  },
  {
    label: "Project Work",
    items: [
      { to: "/controller/projects", label: "Projects", icon: FolderOpen, anyPermission: ["view_projects"] },
      { to: "/controller/estimates", label: "Estimates", icon: FileText, anyPermission: ["edit_contract_sov", "view_projects"] },
    ],
  },
  {
    label: "Financial",
    items: [
      { to: "/controller/approvals", label: "Approvals", icon: ClipboardCheck, anyPermission: ["approve_bills", "approve_payments", "edit_contract_sov"] },
      { to: "/controller/invoices", label: "Invoices", icon: Receipt, anyPermission: ["view_financials", "approve_invoices"] },
      { to: "/controller/bills", label: "Bills", icon: CreditCard, anyPermission: ["view_financials", "approve_bills"] },
      { to: "/controller/expenses", label: "Expenses", icon: Briefcase, anyPermission: ["view_financials", "manage_expenses", "submit_expenses"] },
      { to: "/controller/vendors/1099-report", label: "1099 Report", icon: FileSpreadsheet, anyPermission: ["view_financials", "manage_vendors"] },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/controller/team", label: "Team", icon: UsersRound, anyPermission: ["invite_users", "manage_settings", "view_projects"] },
      { to: "/controller/customers", label: "Customers", icon: Users, anyPermission: ["manage_customers", "view_projects"] },
      { to: "/controller/vendors", label: "Vendors", icon: Truck, anyPermission: ["manage_vendors", "view_projects"] },
      { to: "/controller/compliance", label: "Compliance", icon: ShieldCheck, anyPermission: ["manage_vendors"] },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/controller/periods", label: "Periods", icon: CalendarClock, anyPermission: ["view_financials"] },
      { to: "/controller/audit", label: "Audit Log", icon: ScrollText, anyPermission: ["manage_settings", "view_financials"] },
      { to: "/controller/settings", label: "Settings", icon: Settings, anyPermission: ["manage_settings", "manage_roles", "invite_users"] },
    ],
  },
];

/** Bottom tab bar items — the 4 most important + "More" */
const BOTTOM_TAB_KEYS = ["/controller", "/controller/projects", "/controller/approvals", "/controller/bills"];

const SidebarBrand = () => {
  const { data: profile } = useCompanyProfile();
  const commandxLogo = useCommandXLogo();
  if (profile?.logo_url) {
return <img src={profile.logo_url} alt={profile.name ?? "Company"} className="max-h-[120px] object-contain" />;
  }
  return <img src={commandxLogo} alt="CommandX" className="max-h-[120px] object-contain -my-8" />;
};

const MobileBrand = () => {
  const { data: profile } = useCompanyProfile();
  const commandxLogo = useCommandXLogo();
  if (profile?.logo_url) {
return <img src={profile.logo_url} alt={profile.name ?? "Company"} className="max-h-[84px] object-contain" />;
  }
  return <img src={commandxLogo} alt="CommandX" className="max-h-[84px] object-contain -my-6" />;
};

export const ControllerLayout = () => {
  const { signOut } = useAuth();
  const { can, isAdmin } = usePermissions();
  const { data: approvalBadgeCount } = useApprovalPendingCount();
  const { hasMultipleCompanies, companies, activeCompanyId } = useCompanySelector();
  const location = useLocation();
  const isFullBleed = location.pathname === "/controller/calendar";
  const [moreOpen, setMoreOpen] = useState(false);
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  useBillingCycleNotifications();
  useCalendarRealtime();

  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);

  const filteredGroups = unifiedNav.map((g) => ({
    ...g,
    items: g.items.filter((item) => {
      if (!item.anyPermission) return true;
      if (isAdmin) return true;
      return item.anyPermission.some((p) => can(p));
    }),
  })).filter((g) => g.items.length > 0);

  const allItems = filteredGroups.flatMap((g) => g.items);
  const bottomTabs = allItems.filter((i) => BOTTOM_TAB_KEYS.includes(i.to));

  return (
    <TabProvider registry={controllerTabRegistry}>
    <div className="flex h-screen overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden w-56 shrink-0 border-r bg-card md:flex md:flex-col overflow-y-auto">
        <div className="flex items-center justify-between p-4">
          <SidebarBrand />
          <NotificationBell />
        </div>
        <nav className="flex-1 space-y-4 px-2">
          {filteredGroups.map((group) => (
            <div key={group.label}>
              <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <TabNavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon className="h-4 w-4" />
                        {item.label}
                        {item.to === "/controller/approvals" && (approvalBadgeCount ?? 0) > 0 && (
                          <Badge variant="destructive" className="ml-auto text-[10px] px-1.5 py-0 animate-pulse">
                            {approvalBadgeCount}
                          </Badge>
                        )}
                      </>
                    )}
                  </TabNavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t p-2 space-y-1">
          <TabNavLink
            to="/controller/support-tickets"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium transition-colors",
                isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )
            }
          >
            {() => (
              <>
                <TicketCheck className="h-4 w-4" />
                My Tickets
              </>
            )}
          </TabNavLink>
          <Button variant="ghost" className="w-full justify-start gap-2 text-xs" onClick={() => setReportIssueOpen(true)}>
            <HelpCircle className="h-4 w-4" />
            Report Issue
          </Button>
          {hasMultipleCompanies && (
            <Button variant="ghost" className="w-full justify-start gap-2 text-xs" onClick={() => setCompanySwitcherOpen(true)}>
              <Building2 className="h-4 w-4" />
              <span className="truncate">{activeCompany?.company_name ?? "Switch Workspace"}</span>
            </Button>
          )}
          <Button variant="ghost" className="w-full justify-start gap-2" onClick={signOut}>
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar - minimal */}
        <header className="flex items-center justify-between border-b p-3 md:hidden">
          <MobileBrand />
          <NotificationBell />
        </header>

        {/* Desktop tab bar */}
        <div className="hidden md:block">
          <TabBar />
        </div>

        {/* Mobile: traditional Outlet */}
        <main className={cn("flex-1 overflow-auto md:hidden", isFullBleed ? "pb-16" : "p-2 pb-20")}>
          <Outlet />
        </main>

        {/* Desktop: persistent tab panels */}
        <div className="hidden md:flex md:flex-1 md:overflow-hidden">
          <div className={cn("flex-1 overflow-auto", !isFullBleed && "p-4")}>
            <TabRenderer />
          </div>
        </div>

        {/* Mobile bottom tab bar */}
        <nav className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-around border-t bg-card/95 backdrop-blur-sm py-1 md:hidden safe-area-pb">
          {bottomTabs.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center gap-0.5 px-3 py-1.5 text-[10px] font-medium transition-colors rounded-md min-w-[56px]",
                  isActive ? "text-primary" : "text-muted-foreground"
                )
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label === "Dashboard" ? "Home" : item.label}
              {item.to === "/controller/approvals" && (approvalBadgeCount ?? 0) > 0 && (
                <span className="absolute -top-0.5 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[8px] text-destructive-foreground">
                  {approvalBadgeCount}
                </span>
              )}
            </NavLink>
          ))}

          {/* More button */}
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button className="flex flex-col items-center gap-0.5 px-3 py-1.5 text-[10px] font-medium text-muted-foreground min-w-[56px]">
                <Menu className="h-5 w-5" />
                More
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto rounded-t-2xl">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <nav className="space-y-4 py-4">
                {filteredGroups.map((group) => (
                  <div key={group.label}>
                    <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {group.label}
                    </p>
                    <div className="space-y-0.5">
                      {group.items.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={item.end}
                          onClick={() => setMoreOpen(false)}
                          className={({ isActive }) =>
                            cn(
                              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                              isActive
                                ? "bg-primary/10 text-primary"
                                : "text-foreground hover:bg-accent"
                            )
                          }
                        >
                          <item.icon className="h-5 w-5" />
                          {item.label}
                          {item.to === "/controller/approvals" && (approvalBadgeCount ?? 0) > 0 && (
                            <Badge variant="destructive" className="ml-auto text-[10px] px-1.5 py-0">
                              {approvalBadgeCount}
                            </Badge>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="border-t pt-2 space-y-1">
                  <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => { setMoreOpen(false); setReportIssueOpen(true); }}>
                    <HelpCircle className="h-5 w-5" /> Report Issue
                  </Button>
                  {hasMultipleCompanies && (
                    <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => { setMoreOpen(false); setCompanySwitcherOpen(true); }}>
                      <Building2 className="h-5 w-5" /> Switch Workspace
                    </Button>
                  )}
                  <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => { setMoreOpen(false); signOut(); }}>
                    <LogOut className="h-5 w-5" /> Sign Out
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
      {hasMultipleCompanies && (
        <CompanySelectorModal open={companySwitcherOpen} onSelected={() => window.location.reload()} onOpenChange={setCompanySwitcherOpen} />
      )}
      <ReportIssueDialog open={reportIssueOpen} onOpenChange={setReportIssueOpen} />
    </div>
    </TabProvider>
  );
};
